import { useState, useEffect, useRef } from 'react';
import { Box, Paper, Grid, TextField, Autocomplete, Typography, Button, Dialog, DialogContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter } from '@mui/material';
import axios from 'axios';
import ClientMaster from '../../ClientMaster';
import LeadSourceMaster from './LeadSourceMaster';
import ActivityMaster from './ActivityMaster';
import CurrencyMaster from '../../CurrencyMaster';
import LeadStatusMaster from './LeadStatusMaster';
import UserMaster from '../../UserMaster';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const transform = (data, idKey, valKey) => data.map(item => ({ id: item[idKey], label: item[valKey] }));

const parseDateFlexible = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    // Handle YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dayjs(dateStr, 'YYYY-MM-DD');
    }
    // Handle DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        return dayjs(dateStr, 'DD/MM/YYYY');
    }
    return null;
};

export default function ClientDetails({ initialData = {}, onNext, loggedInUser, companyData, mode, connectionString }) {
    const clientInit = initialData.client ?? initialData;
    console.log('Company search results:', clientInit);
    const API = connectionString;
    const partyDropdownRef = useRef(null);

    // ─── client search state ─────────────────────────────────────────────
    const [companyName, setCompanyName] = useState(initialData.companyName || initialData.FirmName || '');
    const [selectedClientId, setSelectedClientId] = useState(initialData.clientId || null);
    const [searchResults, setSearchResults] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    // ─── other form state ────────────────────────────────────────────────
    const [details, setDetails] = useState(initialData.details || []);
    const [error, setError] = useState('');
    const [quotationNumber, setquotationNumber] = useState(initialData.qoutationNumber || '');
    const [qoutationDate, setqoutationDate] = useState(mode === 'add' || mode === 'order' ? dayjs() : initialData.qoutationDate && dayjs(initialData.qoutationDate, 'DD/MM/YYYY').isValid() ? dayjs(initialData.qoutationDate, 'DD/MM/YYYY') : dayjs());
    const [referenceNumber, setReferenceNumber] = useState(initialData.referenceNumber || '');
    const [referenceDate, setReferenceDate] = useState(mode === 'add' ? null : initialData.referenceDate && parseDateFlexible(initialData.referenceDate) ? parseDateFlexible(initialData.referenceDate) : null);
    const [exchangeRate, setExchangeRate] = useState(initialData.exchangeRate || '');
    const [qnextFollowUpDate, setqnextFollowUpDate] = useState(mode === 'add' || mode === 'order' ? dayjs().add(7, 'day') : initialData.qnextFollowUpDate && dayjs(initialData.qnextFollowUpDate, 'DD/MM/YYYY').isValid() ? dayjs(initialData.qnextFollowUpDate, 'DD/MM/YYYY') : dayjs().add(7, 'day'));
    const [kindAttend, setKindAttend] = useState(initialData.kindAttend || '');
    const [project, setProject] = useState(initialData.project || '');
    const [agentName, setAgentName] = useState(initialData.agentName || '');
    const [agentCommission, setAgentCommission] = useState(initialData.agentCommission || '');
    const [remark, setRemark] = useState(initialData.remark || initialData.remarks || '');

    // ─── masters & popups ────────────────────────────────────────────────
    const [currencyOptions, setCurrencyOptions] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const [leadSourceOptions, setLeadSourceOptions] = useState([]);
    const [selectedLeadSource, setSelectedLeadSource] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [activityOptions, setActivityOptions] = useState([]);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const [openClientPopup, setOpenClientPopup] = useState(false);
    const [openCurrencyPopup, setOpenCurrencyPopup] = useState(false);
    const [openLeadSourcePopup, setOpenLeadSourcePopup] = useState(false);
    const [openEmployeePopup, setOpenEmployeePopup] = useState(false);
    const [openActivityPopup, setOpenActivityPopup] = useState(false);
    const [openStatusPopup, setOpenStatusPopup] = useState(false);

    // ─── Sync referenceDate with qoutationDate only in add mode ───────────
    useEffect(() => {
        if (mode === 'add') {
            setReferenceDate(qoutationDate);
        }
    }, [qoutationDate, mode]);

    // ─── When currencyOptions load or initialData.currencyId changes ──────
    useEffect(() => {
        if (!currencyOptions || currencyOptions.length === 0) return;
        const cid = initialData?.currencyId ?? null;
        if (cid == null) return;
        const found = currencyOptions.find(opt => String(opt.id) === String(cid));
        if (found) {
            setSelectedCurrency(found);
        } else {
            setSelectedCurrency({ id: null, label: '-' });
        }
    }, [currencyOptions, initialData?.currencyId]);

    // ─── Load auto-generated inquiry number ───────────────────────────────
    useEffect(() => {
        if (mode !== 'edit') {
            fetchReceiptNo('Q');
        }
    }, [mode]);

    async function fetchReceiptNo(code) {
        try {
            const { data } = await axios.get(`${API}/api/get-saleOrder-Number`);
            console.log('Fetched sale order number:', data);
            setquotationNumber(data.saleOrderNumber || '');
        } catch (err) {
            console.error('Error fetching sale order number:', err);
            setquotationNumber('');
        }
    }

    // ─── Load all master lists ────────────────────────────────────────────
    useEffect(() => {
        const fetchMasters = async () => {
            try {
                const [cur] = await Promise.all([axios.get(`${API}/currencymaster/search?mode=search`)]);
                const currencyData = transform(cur.data.data || [], 'CurrencyId', 'CurrencySymbol');
                console.log(cur.data.data);
                setCurrencyOptions(currencyData);
                if (initialData.currencyId) {
                    const found = currencyData.find(opt => String(opt.id) === String(initialData.currencyId));
                    setSelectedCurrency(found || { id: null, label: '-' });
                } else {
                    setSelectedCurrency({ id: null, label: '-' });
                }
            } catch (err) {
                console.error('Error fetching masters:', err);
                setCurrencyOptions([]);
                setLeadSourceOptions([]);
                setEmployeeOptions([]);
                setActivityOptions([]);
                setStatusOptions([]);
                setSelectedCurrency({ id: null, label: '-' });
                setSelectedLeadSource({ id: null, label: '-' });
                setSelectedEmployee({ id: null, label: '-' });
                setSelectedActivity({ id: null, label: '-' });
                setSelectedStatus({ id: null, label: '-' });
            }
        };
        fetchMasters();
    }, []);

    // ─── Initialize from initialData ─────────────────────────────────────
    useEffect(() => {
        if (!initialData || Object.keys(initialData).length === 0) return;

        setCompanyName(initialData.companyName || initialData.FirmName || '');
        setSelectedClientId(initialData.clientId ?? null);

        if (initialData.details) {
            if (Array.isArray(initialData.details)) {
                setDetails(initialData.details);
            } else if (typeof initialData.details === 'object') {
                const arr = Object.entries(initialData.details).map(([label, value]) => ({ label, value }));
                setDetails(arr);
            }
        } else {
            const c = {
                city: initialData.city || '',
                state: initialData.state || '',
                mobileNo: initialData.mobileNo || '',
                emailId: initialData.emailId || '',
                contactPerson: initialData.contactPerson || '',
                gstNumber: initialData.gstNumber || ''
            };
            setDetails([
                { label: 'City', value: c.city },
                { label: 'State', value: c.state },
                { label: 'mobileNo', value: c.mobileNo },
                { label: 'Email', value: c.emailId },
                { label: 'ContactPerson', value: c.contactPerson },
                { label: 'GST No.', value: c.gstNumber }
            ]);
        }

        if (mode === 'edit') {
            setqoutationDate(
                initialData.inquiryDate
                    ? parseDateFlexible(initialData.inquiryDate) || dayjs()
                    : dayjs()
            );
            setReferenceDate(
                initialData.referenceDate
                    ? parseDateFlexible(initialData.referenceDate) || null
                    : null
            );
            setqnextFollowUpDate(
                initialData.nextFollowUpDate
                    ? parseDateFlexible(initialData.nextFollowUpDate) || dayjs().add(7, 'day')
                    : dayjs().add(7, 'day')
            );
            setReferenceNumber(initialData.referenceNumber || '');
            setquotationNumber(initialData.qoutationNumber || '');
        } else if (mode === 'order') {
            setqoutationDate(dayjs());
            setReferenceDate(
                initialData.referenceDate
                    ? parseDateFlexible(initialData.referenceDate) || null
                    : null
            );
            setqnextFollowUpDate(
                initialData.nextFollowUpDate
                    ? parseDateFlexible(initialData.nextFollowUpDate) || dayjs().add(7, 'day')
                    : dayjs().add(7, 'day')
            );
            setReferenceNumber(initialData.qoutationNumber || '');
        } else if (mode === 'add') {
            setqoutationDate(dayjs());
            setReferenceDate(null);
            setqnextFollowUpDate(dayjs().add(7, 'day'));
            setReferenceNumber('');
        }

        setExchangeRate(initialData.exchangeRate || '');
        setKindAttend(initialData.kindAttend || '');
        setProject(initialData.project || '');
        setAgentName(initialData.agentName || '');
        setAgentCommission(initialData.agentCommission || '');
        setRemark(initialData.remark || initialData.remarks || '');
    }, [initialData, mode]);

    // ─── Fetch search results ─────────────────────────────────────────────
    useEffect(() => {
        if (!companyName || !dropdownOpen) {
            setSearchResults([]);
            return;
        }

        const fetch = async () => {
            try {
                const res = await axios.get(`${API}/api/company-search`, { params: { field: 'FirmName', search: companyName } });
                const data = Array.isArray(res.data.data) ? res.data.data : [];
                console.log('Company search results:', data);
                setSearchResults(data);
            } catch (err) {
                console.error('Error fetching company search results:', err);
                setSearchResults([]);
            }
        };
        fetch();
    }, [companyName, dropdownOpen]);

    // ─── Handle row selection in table ────────────────────────────────────
    const onSelectResult = r => {
        setSelectedClientId(r.id);
        setCompanyName(r.FirmName || r.companyName || '');
        setAgentName(r.AgentName || '');
        setAgentCommission(r.AgentComm || '');

        const c = {
            City: r.City || '',
            state: r.state || '',
            MobileNo: r.MobileNo || '',
            EmailId: r.EmailId || '',
            ContactPerson: r.ContactPerson || '',
            GSTNo: r.GSTNo || ''
        };
        setDetails([
            { label: 'City', value: c.City },
            { label: 'State', value: c.state },
            { label: 'mobileNo', value: c.MobileNo },
            { label: 'Email', value: c.EmailId },
            { label: 'ContactPerson', value: c.ContactPerson },
            { label: 'GST No.', value: c.GSTNo }
        ]);
        setDropdownOpen(false);
    };

    // ─── Dropdown field for Autocomplete ──────────────────────────────────
    const dropdownField = (label, options, value, setValue) => (
        <Grid item xs={12} sm={8} md={12}>
            <Autocomplete fullWidth options={options} getOptionLabel={opt => opt?.label || ''} value={value} onChange={(_, v) => setValue(v)} isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)} disabled={!isEditable}
                renderInput={params => (
                    <TextField {...params} label={label} size="small" InputLabelProps={{ shrink: Boolean(value) }}
                        sx={{
                            '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                            '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                        }}
                    />
                )}
            />
        </Grid>
    );

    // ─── Next button ─────────────────────────────────────────────────────
    const handleNext = () => {
        if (!companyName && !quotationNumber) {
            setError('Party & Order No. are required.');
            return;
        }
        if (qnextFollowUpDate && qoutationDate && qnextFollowUpDate.isValid() && qoutationDate.isValid() && qnextFollowUpDate.isBefore(qoutationDate)) {
            setError('Next follow-up must be after order date.');
            return;
        }
        setError('');

        console.log('Selected currency (state):', selectedCurrency);
        console.log('Exchange rate (state):', exchangeRate);
        console.log('kindAttend, project, remark:', kindAttend, project, remark);

        const payload = {
            clientId: selectedClientId,
            companyName,
            inquiryDate: qoutationDate && qoutationDate.isValid() ? qoutationDate.format('YYYY-MM-DD') : '',
            referenceNumber,
            referenceDate: referenceDate && referenceDate.isValid() ? referenceDate.format('YYYY-MM-DD') : '',
            editreferenceNumber: initialData?.editreferenceNumber || '',
            currencyId: selectedCurrency?.id ?? null,
            exchangeRate: exchangeRate === '' ? '' : Number(exchangeRate),
            nextFollowUpDate: qnextFollowUpDate && qnextFollowUpDate.isValid() ? qnextFollowUpDate.format('YYYY-MM-DD') : '',
            kindAttend,
            remarks: remark,
            project,
            selectedEmployee: selectedEmployee?.id ?? null,
            selectedLeadSource: selectedLeadSource?.id ?? null,
            selectedActivity: selectedActivity?.id ?? null,
            selectedStatus: selectedStatus?.id ?? null,
            agentName,
            agentCommission,
            details: details.reduce((acc, curr) => {
                acc[curr.label] = curr.value;
                return acc;
            }, {}),
        };

        console.log('ClientDetails onNext payload:', payload);
        onNext(payload);
    };

    // ─── Allow editing only in 'add' or 'edit' modes ─────────────────────
    const isEditable = mode === 'add' || mode === 'edit' || mode === 'order';

    return (
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
            <Box display="flex" justifyContent="space-between" flexWrap="wrap" pt={1}>
                <Box sx={{ width: { xs: "100%", md: "calc(50% - 16px)" }, pr: { md: 3 } }}>
                    {/* Party dropdown */}
                    <Box ref={partyDropdownRef} tabIndex={-1} sx={{ position: 'relative', flex: '1 1 40%' }}
                        onBlur={e => {
                            if (!partyDropdownRef.current.contains(e.relatedTarget)) {
                                setDropdownOpen(false);
                            }
                        }}
                    >
                        <TextField disabled={!isEditable} fullWidth size="small" label="Party Name" variant="outlined" value={companyName} required InputLabelProps={{ shrink: Boolean(companyName) }}
                            sx={{
                                '& .MuiOutlinedInput-root': { height: '40px', display: 'flex', alignItems: 'center' },
                                '& .MuiOutlinedInput-input': { padding: '0 12px' }
                            }}
                            onChange={e => {
                                setCompanyName(e.target.value);
                                setDropdownOpen(true);
                            }}
                        />
                        {dropdownOpen && (
                            <TableContainer component={Paper} elevation={0} sx={{ mt: 1, maxHeight: 240, position: 'absolute', width: '120%', zIndex: 10, border: 'none', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1 }} >
                                <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: 0, '& thead th': { backgroundColor: '#1877F2', color: '#fff', fontWeight: 600, border: 'none' } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Company</TableCell>
                                            <TableCell>GST No</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell>Mobile</TableCell>
                                            <TableCell>City</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{ '& > tr:last-of-type': { borderBottom: '1px solid rgba(0,0,0,0.12)' } }}>
                                        {searchResults.length > 0 ? (
                                            searchResults.map((r, i) => (
                                                <TableRow key={i} hover onMouseDown={() => onSelectResult(r)} sx={{ cursor: 'pointer', backgroundColor: i % 2 === 0 ? '#fff' : 'rgba(227, 242, 253, 1)', border: 'none' }} >
                                                    <TableCell>{r.FirmName}</TableCell>
                                                    <TableCell>{r.GSTNo}</TableCell>
                                                    <TableCell>{r.ContactPerson}</TableCell>
                                                    <TableCell>{r.MobileNo}</TableCell>
                                                    <TableCell>{r.City}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">No Data Available</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                    <TableFooter sx={{ borderTop: '1px solid rgba(0,0,0,0.12)' }}>
                                        <TableRow hover sx={{ cursor: 'pointer', backgroundColor: '#fff' }} onMouseDown={() => setOpenClientPopup(true)}>
                                            <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'primary.main' }}>
                                                ➕ Add New Client
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>

                    {/* Details display */}
                    <Grid item xs={12} md={4} pt={2}>
                        <TextField fullWidth size="small" label="Address" variant="outlined" disabled value={Array.isArray(details) ? details.map(d => `${d.label}: ${d.value}`).join(', ') : ''} multiline rows={3.4} InputLabelProps={{ shrink: Array.isArray(details) && details.length > 0 }}
                            sx={{
                                '& .MuiOutlinedInput-root': { display: 'flex', alignItems: 'center' },
                                '& .MuiOutlinedInput-input': { padding: '0 12px' }
                            }}
                        />
                    </Grid>

                    <Box display="flex" gap={2} flex="1 1 40%" pt={2}>
                        <Box sx={{ flex: '0 0 48%' }}>
                            <TextField fullWidth size="small" label="Kind Attend" value={kindAttend} onChange={e => setKindAttend(e.target.value)} InputLabelProps={{ shrink: Boolean(kindAttend) }} disabled={!isEditable}
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                }}
                            />
                        </Box>
                        <Box sx={{ flex: '0 0 48%' }}>
                            <TextField fullWidth size="small" label="Project" value={project} onChange={e => setProject(e.target.value)} InputLabelProps={{ shrink: Boolean(project) }} disabled={!isEditable}
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <TextField fullWidth size="small" label="Remark" disabled={!isEditable} multiline minRows={3} maxRows={6} value={remark} InputLabelProps={{ shrink: Boolean(remark) }} inputProps={{ maxLength: 250 }}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val.length <= 250) setRemark(val);
                                else setRemark(val.slice(0, 250));
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': { height: 'auto', alignItems: 'flex-start' },
                                '& .MuiOutlinedInput-input': { padding: '10px 12px' }
                            }}
                        />
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'right', color: 'text.secondary' }}>
                            {remark.length}/250
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ width: { xs: "100%", md: "calc(50% - 46px)" }, pt: { xs: 2, md: 0 } }} alignSelf="right">
                    <Box display="flex" gap={2} flex="1 1 40%">
                        <Box sx={{ flex: '0 0 48%' }}>
                            <TextField fullWidth size="small" label="Order No." variant='outlined' value={quotationNumber} disabled InputLabelProps={{ shrink: true }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                    '& .MuiOutlinedInput-input': { padding: '0 12px' }
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: '0 0 48%' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <DatePicker label="Order Date" value={qoutationDate} onChange={setqoutationDate} disabled
                                    slots={{
                                        textField: (params) => (
                                            <TextField {...params} size="small" variant="outlined" value={qoutationDate ? dayjs(qoutationDate).format('DD/MM/YYYY') : ''} InputLabelProps={{ shrink: true }} disabled
                                                sx={{
                                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                                    '& .MuiOutlinedInput-input': { padding: '0 12px' }
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>

                    <Box display="flex" gap={2} flex="1 1 40%" pt={2}>
                        <Box sx={{ flex: '0 0 48%' }}>
                            <TextField fullWidth size="small" variant='outlined' label="Ref. No." value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} InputLabelProps={{ shrink: Boolean(referenceNumber) }} disabled={!isEditable}
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: '0 0 48%' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <DatePicker
                                    label="Ref. Date"
                                    value={referenceDate}
                                    onChange={(newValue) => setReferenceDate(newValue)}
                                    disabled={!isEditable}
                                    slots={{
                                        textField: (params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                value={referenceDate && referenceDate.isValid() ? referenceDate.format('DD/MM/YYYY') : ''}
                                                InputLabelProps={{ shrink: Boolean(referenceDate) || mode === 'add' }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>

                    <Box display="flex" gap={2} flex="1 1 40%" pt={2}>
                        <Box sx={{ flex: '0 0 48%', display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ flex: '0 0 50%' }}>
                                {dropdownField('Currency', currencyOptions, selectedCurrency, setSelectedCurrency)}
                            </Box>
                            <Box sx={{ flex: '0 0 47%' }}>
                                <TextField fullWidth size="small" type="number" variant="outlined" label="Ex Rate" value={exchangeRate} InputLabelProps={{ shrink: Boolean(exchangeRate) }} disabled={!isEditable}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (/^\d*\.?\d{0,2}$/.test(value)) {
                                            setExchangeRate(value);
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                        '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* <Box sx={{ flex: '0 0 48%' }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                                <DatePicker minDate={qoutationDate} label="Follow Up Date" value={qnextFollowUpDate} onChange={(newValue) => setqnextFollowUpDate(newValue)} disabled={!isEditable}
                                    slots={{
                                        textField: (params) => (
                                            <TextField {...params} fullWidth size="small" variant="outlined" value={qnextFollowUpDate ? qnextFollowUpDate.format('DD/MM/YYYY') : ''} InputLabelProps={{ shrink: Boolean(qnextFollowUpDate) }}
                                                sx={{
                                                    '& .MuiOutlinedInput-root': { height: 40, display: 'flex', alignItems: 'center' },
                                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 12px' }
                                                }}
                                            />
                                        )
                                    }}
                                />
                            </LocalizationProvider>
                        </Box> */}
                    </Box>

                    <Box display="flex" gap={2} flex="1 1 40%" pt={2}>
                        <Box sx={{ flex: '0 0 48%' }}>
                            <TextField fullWidth label="Agent Name" value={agentName} onChange={e => setAgentName(e.target.value)} InputLabelProps={{ shrink: Boolean(agentName) }} variant="outlined" size="small" disabled={!isEditable}
                                sx={{
                                    '& .MuiOutlinedInput-root': { backgroundColor: 'white', height: '40px' },
                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 10px' }
                                }}
                            />
                        </Box>

                        <Box sx={{ flex: '0 0 48%' }}>
                            <TextField fullWidth label="Agent Commission (%)" value={agentCommission} InputLabelProps={{ shrink: Boolean(agentCommission) }} variant="outlined" size="small" type="number" disabled={!isEditable}
                                onChange={e => {
                                    const v = e.target.value;
                                    if (v === '') {
                                        setAgentCommission('');
                                        return;
                                    }
                                    const re = /^(?:100(?:\.(?:0{0,2}))?|[0-9]{1,2}(?:\.\d{0,2})?)$/;
                                    if (re.test(v)) {
                                        setAgentCommission(v);
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': { backgroundColor: 'white', height: '40px' },
                                    '& .MuiOutlinedInput-input': { fontSize: '0.875rem', padding: '0 10px' }
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>

            {!!error && (
                <Typography color="error" mt={2}>
                    {error}
                </Typography>
            )}

            <Box mt={2} display="flex" justifyContent="flex-end">
                <Button variant="contained" onClick={handleNext}>Next</Button>
            </Box>

            {/* Popups */}
            <Dialog open={openClientPopup} fullWidth maxWidth="md" onClose={() => setOpenClientPopup(false)}>
                <DialogContent>
                    <ClientMaster mode="add" onPopupCancel={() => setOpenClientPopup(false)} connectionString={connectionString} />
                </DialogContent>
            </Dialog>
            <Dialog open={openCurrencyPopup} onClose={() => setOpenCurrencyPopup(false)}>
                <DialogContent>
                    <CurrencyMaster mode="add" onPopupCancel={() => setOpenCurrencyPopup(false)} connectionString={connectionString} />
                </DialogContent>
            </Dialog>
            <Dialog open={openLeadSourcePopup} onClose={() => setOpenLeadSourcePopup(false)}>
                <DialogContent>
                    <LeadSourceMaster mode="add" onPopupCancel={() => setOpenLeadSourcePopup(false)} connectionString={connectionString} />
                </DialogContent>
            </Dialog>
            <Dialog open={openEmployeePopup} onClose={() => setOpenEmployeePopup(false)}>
                <DialogContent>
                    <UserMaster mode="add" onPopupCancel={() => setOpenEmployeePopup(false)} connectionString={connectionString} />
                </DialogContent>
            </Dialog>
            <Dialog open={openActivityPopup} onClose={() => setOpenActivityPopup(false)}>
                <DialogContent>
                    <ActivityMaster mode="add" onPopupCancel={() => setOpenActivityPopup(false)} connectionString={connectionString} />
                </DialogContent>
            </Dialog>
            <Dialog open={openStatusPopup} onClose={() => setOpenStatusPopup(false)}>
                <DialogContent>
                    <LeadStatusMaster mode="add" onPopupCancel={() => setOpenStatusPopup(false)} connectionString={connectionString} />
                </DialogContent>
            </Dialog>
        </Paper>
    );
}